import { VariantProps } from "class-variance-authority";
import { PhoneOff } from "lucide-react";
import { forwardRef, useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useIsModerator } from "@/features/participants/api/is-moderator";
import { useStopRecording } from "@/features/recordings/api/toggle-recording";
import { useVidyoStore } from "@/features/vidyo-call/utils/vidyo-store";

import { useEndSessionForEveryone } from "../../api/end-session-for-everyone";
import { ActionButton, ActionButtonProps } from "../action-button";

type LeaveCallProps = Omit<ActionButtonProps, "Icon" | "text"> &
  VariantProps<typeof ActionButton> & {
    size?: "fixed" | "auto";
  };
let recordingToastId: number | string = "";
let endSessionToastId: number | string = "";

export const LeaveCall = forwardRef<HTMLButtonElement, LeaveCallProps>(
  ({ size = "fixed", ...props }, ref) => {
    const { key } = useParams();
    const navigate = useNavigate();
    const vidyoSDK = useVidyoStore((state) => state.vidyoSDK);
    const isRecording = useVidyoStore((state) => state.isRecording);

    const isModerator = useIsModerator();
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const {
      mutateAsync: endSessionForEveryone,
      isPending: isEndingSessionForEveryone,
    } = useEndSessionForEveryone({
      onMutate: () => {
        endSessionToastId = toast.loading("Ending session for everyone...");
      },
      onSuccess: () => {
        toast.success("Session ended for everyone", {
          id: endSessionToastId,
        });
      },
      onError: () => {
        toast.error("Failed to end session for everyone", {
          id: endSessionToastId,
        });
      },
    });
    const sessionId = useVidyoStore((state) => state.sessionId);
    const { mutateAsync: stopRecording, isPending: isStoppingRecording } =
      useStopRecording({
        onMutate: () => {
          recordingToastId = toast.loading("Stopping recording...");
        },
        onSuccess: () => {
          toast.success("Recording stopped", {
            id: recordingToastId,
          });
        },
        onError: () => {
          toast.error("Failed to stop recording", {
            id: recordingToastId,
          });
        },
      });

    const disconnect = useCallback(async () => {
      await vidyoSDK?.Disconnect();

      navigate(`/r/${key}/after-call`);
    }, [vidyoSDK, key, navigate]);

    const handleClosePopover = useCallback(() => {
      setIsPopoverOpen(false);
    }, []);

    const handleEndSessionForEveryone = async () => {
      try {
        if (isRecording) {
          await stopRecording({ sessionId });
        }
        await endSessionForEveryone(sessionId);
      } catch (error) {
        console.error(error);
      }
    };

    return (
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <PopoverTrigger asChild>
          <ActionButton
            {...props}
            ref={ref}
            size={size}
            Icon={PhoneOff}
            text="Leave Session"
            variant="leave-call"
            data-testid="leave-call"
            key="leave-session"
            className="w-[115px]"
          />
        </PopoverTrigger>
        <PopoverContent className="w-[230px] space-y-2">
          {isModerator && (
            <Button
              onClick={handleEndSessionForEveryone}
              variant="destructive"
              className="w-full"
              isLoading={isEndingSessionForEveryone || isStoppingRecording}
              isAnimated={isEndingSessionForEveryone || isStoppingRecording}
            >
              End session for everyone
            </Button>
          )}
          <Button onClick={disconnect} className="w-full" variant="outline">
            Leave session
          </Button>
          {!isModerator && (
            <Button
              onClick={handleClosePopover}
              className="w-full"
              variant="ghost"
            >
              Cancel
            </Button>
          )}
        </PopoverContent>
      </Popover>
    );
  },
);

LeaveCall.displayName = "LeaveCall";
